/**
 * TPS et TVQ de la caisse, en cents entiers, sans flottant (plan, section 1.1).
 *
 * Methode : METHODES_TAXES.LIGHTSPEED_SOMME_NON_ARRONDIE (vente/contrat.js),
 * choisie d'apres 50 vraies ventes Lightspeed
 * (vente/__tests__/taxesVentesReelles.test.js) :
 *   - taxe de la VENTE = arrondi au cent de la somme des taxes NON arrondies de
 *     toutes les lignes (taux combine 14,975 % ou TPS 5 %) : 50 ventes sur 50 ;
 *     la somme des taxes de ligne arrondies n'en donne que 35 sur 50 ;
 *   - taxe d'une LIGNE = arrondi au cent de (base x taux combine) : 248 lignes
 *     sur 248 ;
 *   - arrondi au plus proche, moitie vers le haut (domain/money.js) ; aucun cas
 *     a la moitie exacte dans l'echantillon, l'arrondi bancaire n'est donc pas
 *     exclu par les donnees.
 *
 * Separation TPS / TVQ (Lightspeed ne donne que la taxe combinee) :
 *   TPS = arrondi au cent de (somme des bases taxables x 5 %),
 *   TVQ = taxe combinee arrondie - TPS.
 * La taxe totale et le total sont donc exactement ceux de Lightspeed. Limite :
 * la TVQ affichee peut differer d'un cent de l'arrondi de (base TVQ x 9,975 %)
 * calcule seul ; la somme TPS + TVQ, elle, ne differe jamais. La meme regle
 * s'applique a chaque ligne (tps_cents + tvq_cents = taxe de ligne Lightspeed).
 */
import { TAUX_TAXES } from '../vente/contrat';
import { diviserArrondi, versNombre } from './money';

const PPM = BigInt(1000000);
const TPS = BigInt(TAUX_TAXES.TPS_PPM);
const COMBINE = BigInt(TAUX_TAXES.TPS_PPM + TAUX_TAXES.TVQ_PPM);

/** Taux total d'un code de taxe, en millioniemes. */
function tauxTotal(taxCode) {
    if (taxCode === 'NONE') return BigInt(0);
    if (taxCode === 'TPS') return TPS;
    if (taxCode === 'TPS_TVQ') return COMBINE;
    throw new RangeError(`code de taxe inconnu : ${taxCode}`);
}

function base(v) {
    if (typeof v === 'bigint') return v;
    if (!Number.isSafeInteger(v)) throw new TypeError('base : entier attendu');
    return BigInt(v);
}

/**
 * Taxes de la vente a partir des bases de ses lignes.
 * @param {{ base: number|bigint, tax_code: 'NONE'|'TPS'|'TPS_TVQ' }[]} lignes base hors taxe, apres rabais
 * @param {{ unitesParCent?: number }} [options] base exprimee en 1/unitesParCent de cent (1 = cents ;
 *   1000 = dix-millieme de dollar, pour les prix de Lightspeed a 5 decimales)
 * @returns {{ tps_cents: number, tvq_cents: number, tax_cents: number }}
 */
export function taxesVente(lignes, { unitesParCent = 1 } = {}) {
    if (!Array.isArray(lignes)) throw new TypeError('lignes : tableau attendu');
    const diviseur = PPM * BigInt(unitesParCent);
    let combine = BigInt(0);
    let tps = BigInt(0);
    for (const l of lignes) {
        const b = base(l.base);
        if (b < BigInt(0)) throw new RangeError('base negative');
        combine += b * tauxTotal(l.tax_code);
        if (l.tax_code !== 'NONE') tps += b * TPS;
    }
    const taxCents = versNombre(diviserArrondi(combine, diviseur));
    const tpsCents = versNombre(diviserArrondi(tps, diviseur));
    return { tps_cents: tpsCents, tvq_cents: taxCents - tpsCents, tax_cents: taxCents };
}

/**
 * Taxes d'une ligne (affichage et ERP), selon la meme regle.
 * @param {number} netCents base de la ligne, apres rabais
 * @param {'NONE'|'TPS'|'TPS_TVQ'} taxCode
 * @returns {{ tps_cents: number, tvq_cents: number }}
 */
export function taxesLigne(netCents, taxCode) {
    const { tps_cents: tpsCents, tvq_cents: tvqCents } = taxesVente([{ base: netCents, tax_code: taxCode }]);
    return { tps_cents: tpsCents, tvq_cents: tvqCents };
}

/**
 * Somme des taxes de ligne arrondies (METHODES_TAXES.PAR_LIGNE). Gardee pour
 * mesurer l'ecart avec Lightspeed dans les tests, pas pour facturer.
 */
export function taxesParLigne(lignes, options) {
    return lignes.reduce((acc, l) => {
        const t = taxesVente([l], options);
        return {
            tps_cents: acc.tps_cents + t.tps_cents,
            tvq_cents: acc.tvq_cents + t.tvq_cents,
            tax_cents: acc.tax_cents + t.tax_cents,
        };
    }, { tps_cents: 0, tvq_cents: 0, tax_cents: 0 });
}

/**
 * Code de taxe d'un article d'apres ses indicateurs dans l'ERP : la regle de
 * backend/src/services/tax/itemTaxCode.js (resolveTaxCode), recopiee pour les
 * tests contre les ventes reelles. La caisse recoit le code tout fait dans le
 * catalogue (tax_code) et ne s'en sert pas pour vendre.
 *   taxable = 0 -> NONE ; taxable = 1 et tvq_detaxee -> TPS ; taxable = 1 -> TPS_TVQ ;
 *   autre valeur -> null.
 */
export function codeTaxeDepuisDrapeaux({ taxable, tvqDetaxee }) {
    if (taxable === 0 || taxable === false) return 'NONE';
    if (taxable === 1 || taxable === true) return tvqDetaxee ? 'TPS' : 'TPS_TVQ';
    return null;
}
